import { useEffect, useMemo, useState } from 'react';
import { BookOpen, Compass, Loader2, ScrollText } from 'lucide-react';
import type { PublicPublishLanding, PublicPublishRouteView } from '../../../shared/types';
import { api } from '../../api/client';
import MarkdownContent from '../ui/MarkdownContent';

type ParsedPublicPath = {
  campaignId: number;
  spaceSlug: string;
  routePath: string | null;
};

const parsePublicPath = (pathname: string): ParsedPublicPath | null => {
  const match = pathname.match(/^\/public\/campaigns\/(\d+)\/spaces\/([^/]+)(?:\/routes\/(.*))?\/?$/);
  if (!match) return null;

  const campaignId = Number.parseInt(match[1], 10);
  if (!Number.isFinite(campaignId) || campaignId <= 0) return null;

  const routeTail = typeof match[3] === 'string' && match[3].trim()
    ? `/${decodeURIComponent(match[3]).replace(/^\/+/, '')}`
    : null;

  return {
    campaignId,
    spaceSlug: decodeURIComponent(match[2]),
    routePath: routeTail,
  };
};

const encodeRoutePath = (routePath: string): string =>
  routePath
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
    .map((segment) => encodeURIComponent(segment))
    .join('/');

const buildSpaceHref = (campaignId: number, spaceSlug: string): string =>
  `/public/campaigns/${campaignId}/spaces/${encodeURIComponent(spaceSlug)}`;

const buildRouteHref = (campaignId: number, spaceSlug: string, routePath: string): string =>
  `${buildSpaceHref(campaignId, spaceSlug)}/routes/${encodeRoutePath(routePath)}`;

const routeIsActive = (currentPath: string | null, routePath: string): boolean =>
  Boolean(currentPath && currentPath === routePath);

export default function PublicPublishPage() {
  const [pathname, setPathname] = useState(window.location.pathname);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [landing, setLanding] = useState<PublicPublishLanding | null>(null);
  const [routeView, setRouteView] = useState<PublicPublishRouteView | null>(null);

  const parsed = useMemo(() => parsePublicPath(pathname), [pathname]);
  const display = routeView ?? landing;
  const themeClass = display?.theme.className || 'public-theme-atlas';

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (!parsed) {
      setIsLoading(false);
      setError('This public publishing URL is invalid.');
      return;
    }

    let isCancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        if (parsed.routePath) {
          const routePayload = await api.getPublicPublishRoute(
            parsed.campaignId,
            parsed.spaceSlug,
            parsed.routePath
          );
          if (isCancelled) return;
          setRouteView(routePayload);
          setLanding(null);
        } else {
          const landingPayload = await api.getPublicPublishLanding(parsed.campaignId, parsed.spaceSlug);
          if (isCancelled) return;
          setLanding(landingPayload);
          setRouteView(null);
        }
      } catch (loadError) {
        if (isCancelled) return;
        setLanding(null);
        setRouteView(null);
        setError(loadError instanceof Error ? loadError.message : 'Unable to load public publication.');
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      isCancelled = true;
    };
  }, [parsed]);

  if (isLoading) {
    return (
      <div className="public-shell public-theme-atlas">
        <div className="public-loading">
          <Loader2 size={28} className="animate-spin" />
          <span>Loading public codex...</span>
        </div>
      </div>
    );
  }

  if (error || !parsed || !display) {
    return (
      <div className="public-shell public-theme-atlas">
        <div className="public-error">
          <h1>Publication Unavailable</h1>
          <p>{error || 'This published page could not be found.'}</p>
        </div>
      </div>
    );
  }

  const currentRoutePath = parsed.routePath;

  return (
    <div className={`public-shell ${themeClass}`}>
      <header className="public-hero">
        <div className="public-kicker">
          <Compass size={14} />
          Published Campaign Codex
        </div>
        <h1>{display.space.name}</h1>
        <p>{display.space.description || 'A curated public archive from this campaign world.'}</p>
      </header>

      <div className="public-layout">
        <aside className="public-nav">
          <div className="public-nav-title">
            <BookOpen size={15} />
            Routes
          </div>
          {display.routes.length === 0 ? (
            <div className="public-muted">No published pages are available.</div>
          ) : (
            <div className="public-route-list">
              {display.routes.map((entry) => (
                <a
                  key={entry.path}
                  href={buildRouteHref(display.campaignId, display.space.slug, entry.path)}
                  className={`public-route-link ${routeIsActive(currentRoutePath, entry.path) ? 'public-route-active' : ''}`}
                >
                  <div className="public-route-label">{entry.label}</div>
                  {entry.description && <div className="public-route-desc">{entry.description}</div>}
                </a>
              ))}
            </div>
          )}
        </aside>

        <main className="public-content">
          {!routeView && (
            <section className="public-landing">
              <h2>Choose a Published Page</h2>
              <p>
                This space is themed as <strong>{display.theme.label}</strong>.
                Select a route from the left panel to open its article.
              </p>
            </section>
          )}

          {routeView && (
            <article className="public-entity">
              <div className="public-entity-meta">
                <ScrollText size={14} />
                <span>{routeView.entity.subtitle || routeView.entity.entityType}</span>
              </div>
              <h2>{routeView.entity.title}</h2>

              {routeView.entity.summary && (
                <div className="public-summary">
                  <MarkdownContent content={routeView.entity.summary} />
                </div>
              )}

              {routeView.entity.sections.length > 0 ? (
                <div className="public-sections">
                  {routeView.entity.sections
                    .filter((section) => section.content.trim().length > 0)
                    .map((section, index) => (
                      <section key={`${section.type}-${section.title}-${index}`} className="public-section-card">
                        <h3>{section.title || 'Section'}</h3>
                        <MarkdownContent content={section.content} />
                      </section>
                    ))}
                </div>
              ) : routeView.entity.content.trim() ? (
                <section className="public-section-card">
                  <h3>Content</h3>
                  <MarkdownContent content={routeView.entity.content} />
                </section>
              ) : (
                <div className="public-muted">No published text for this page.</div>
              )}
            </article>
          )}
        </main>
      </div>
    </div>
  );
}
